import {css} from '@mui/material';
import React, {useMemo} from 'react';
import {useIntl} from 'react-intl';

import {parseFilterData} from './alert_format';
import {AlertEpisodeTooltipDto} from './alert_types';
import {useFormatters} from '../LightFormatter';

/** One criterion of an alert definition as the tooltips show it. */
export type AlertCriteriaChip = {
  key: string;
  label: string;
  value: string;
};

/** 'panel' is the episode tooltip's chip (11px, 6px radius); 'board' the alert board's definition tooltip (12px pill). */
export type AlertCriteriaChipVariant = 'panel' | 'board';
export type AlertCriteriaRenderOptions = {variant?: AlertCriteriaChipVariant};

/** Renders the criteria of an alert definition; the product supplies it to the tooltip so the chips read exactly
 * like the "Active filters" of the list the filter was saved on. */
export type AlertCriteriaRenderer = (
  filterComponent: string,
  filterData: AlertEpisodeTooltipDto['filterData'],
  options?: AlertCriteriaRenderOptions
) => React.ReactNode;

const chipsCss = css`
  display: flex;
  flex-wrap: wrap;
  gap: 7px;
  &[data-variant='board'] {
    gap: 8px;
  }
`;
const chipCss = css`
  font-family: var(--dlFontFamilySemiBold);
  font-size: 11px;
  color: var(--dlThemeGray-600);
  background: var(--dlThemeGray-50);
  border-radius: 6px;
  padding: 3px 8px;
  max-width: 100%;
  overflow-wrap: anywhere;
  /* the board's definition tooltip (Alert Board design): a grey pill in the body font */
  &[data-variant='board'] {
    display: inline-flex;
    align-items: center;
    font-family: var(--dlFontFamilyNormal);
    font-size: 12px;
    color: var(--dlThemeGray-700);
    background: var(--dlThemeGray-100);
    border-radius: 16px;
    padding: 4px 12px;
  }
`;
const emptyCss = css`
  font-size: 11.5px;
  color: var(--dlThemeGray-400);
`;

/** The grey chips of the design (`.ctc`), one per criterion, or the "no criteria" line. */
export const AlertCriteriaChipList: React.FC<{chips: AlertCriteriaChip[]; variant?: AlertCriteriaChipVariant}> = ({
  chips,
  variant = 'panel',
}) => {
  const intl = useIntl();
  if (!chips.length) return <span css={emptyCss}>{intl.formatMessage({id: 'alert.episode.tooltip.noCriteria'})}</span>;
  return (
    <span css={chipsCss} data-variant={variant}>
      {chips.map((chip) => (
        <span css={chipCss} key={chip.key} data-name={'dl-alert-episode-tooltip--chip'} data-key={chip.key} data-variant={variant}>
          {chip.label}: {chip.value}
        </span>
      ))}
    </span>
  );
};

AlertCriteriaChipList.displayName = 'AlertCriteriaChipList';

/** Filter keys that describe the page rather than the criteria; never shown as chips. */
const NON_CRITERIA_KEYS = new Set(['page', 'pageSize', 'sort', 'sorting', 'paging']);

/**
 * The fallback chips for a saved-filter component without a product renderer: one chip per raw filter key
 * with a value, labelled from the saved-filter vocabulary where a translation exists
 * (`filter.<component>.<key>`, then `filter.<key>`) and by the key otherwise.
 */
export function useGenericAlertCriteriaChips(
  filterComponent: string,
  filterData: AlertEpisodeTooltipDto['filterData']
): AlertCriteriaChip[] {
  const intl = useIntl();
  const {tEnum} = useFormatters();
  return useMemo(() => {
    const data = parseFilterData(filterData);
    const chips: AlertCriteriaChip[] = [];
    Object.entries(data).forEach(([key, raw]) => {
      if (NON_CRITERIA_KEYS.has(key)) return;
      if (raw === null || raw === undefined || raw === '' || (Array.isArray(raw) && raw.length === 0)) return;
      if (typeof raw === 'object' && !Array.isArray(raw) && Object.keys(raw as object).length === 0) return;
      const candidates = [`filter.${filterComponent}.${key}`, `filter.${key}`];
      const labelKey = candidates.find((id) => Boolean(intl.messages[id]));
      const label = labelKey ? intl.formatMessage({id: labelKey}) : key;
      const format = (v: unknown): string => {
        if (typeof v === 'boolean') return intl.formatMessage({id: v ? 'global.yes' : 'global.no'});
        if (typeof v === 'string') {
          const translated = tEnum(undefined, v);
          return translated && translated !== v ? translated : v;
        }
        if (typeof v === 'object' && v !== null) return JSON.stringify(v);
        return String(v);
      };
      const value = Array.isArray(raw) ? raw.map(format).join(', ') : format(raw);
      chips.push({key, label, value});
    });
    return chips;
  }, [filterComponent, filterData, intl, tEnum]);
}

/** The generic chips as a component, the tooltip's default when the product gives it no renderer. */
export const AlertGenericCriteriaChips: React.FC<{
  filterComponent: string;
  filterData: AlertEpisodeTooltipDto['filterData'];
  variant?: AlertCriteriaChipVariant;
}> = (props) => {
  const chips = useGenericAlertCriteriaChips(props.filterComponent, props.filterData);
  return <AlertCriteriaChipList chips={chips} variant={props.variant} />;
};

AlertGenericCriteriaChips.displayName = 'AlertGenericCriteriaChips';
